/**
 * TD PDF Viewer Web Component
 * Renders as a clickable link that opens PDFs in a modal viewer
 *
 * @class TdPdfViewer
 * @extends HTMLElement
 */
class TdPdfViewer extends HTMLElement {
  static get observedAttributes() {
    return ['modal-id'];
  }

  connectedCallback() {
    this.initializeComponent();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue !== newValue && this.isConnected) {
      this.render();
    }
  }

  initializeComponent() {
    this.loadPdfJsLibrary();
    this.render();
    this.setupEventListeners();
  }

  render() {
    const anchor = this.querySelector('a.sb-product-testing__link');
    if (!anchor) return;

    const modalId = this.getAttribute('modal-id');
    if (modalId) {
      anchor.setAttribute('data-modal-id', modalId);
    }

    anchor.setAttribute('role', 'button');
    anchor.setAttribute('aria-label', `Open ${anchor.textContent.trim()} in PDF viewer`);
  }

  setupEventListeners() {
    this.addEventListener('click', this.handleClick.bind(this));
  }

  loadPdfJsLibrary() {
    if (!window.pdfjsLib && !document.querySelector('script[src*="pdf.min.js"]')) {
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.9.179/pdf.min.js';
      script.defer = true;
      document.head.appendChild(script);
    }
  }

  handleClick(event) {
    const anchor = event.target.closest('a.sb-product-testing__link');
    if (!anchor) return;

    event.preventDefault();

    const href = anchor.href;
    const modalId = anchor.getAttribute('data-modal-id');

    if (!href || !modalId) {
      console.warn('sb-pdf-viewer: Missing href or modal-id attribute');
      return;
    }

    this.openPdfModal(href, modalId);
  }

  async openPdfModal(pdfUrl, modalId) {
    try {
      const pdfTemplate = document.getElementById('sb-pdf-viewer-template');
      if (!pdfTemplate) {
        console.error('PDF viewer template not found');
        window.open(pdfUrl, '_blank');
        return;
      }

      const tempContainer = document.createElement('div');
      const clone = pdfTemplate.content.cloneNode(true);
      tempContainer.appendChild(clone);

      this.dispatchModalUpdate(modalId, tempContainer.innerHTML);
      this.dispatchModalOpen(modalId);

      setTimeout(() => this.initPdfViewer(pdfUrl), 300);
    } catch (error) {
      console.error('Error opening PDF modal:', error);
      window.open(pdfUrl, '_blank');
    }
  }

  /**
   * Initialize PDF viewer — renders ALL pages stacked vertically
   * with a scroll-aware "Page X of Y" indicator.
   * @param {string} pdfUrl - URL of the PDF to render
   */
  // async initPdfViewer(pdfUrl) {
  //   if (!window.pdfjsLib) {
  //     console.error('PDF.js library not found');
  //     window.open(pdfUrl, '_blank');
  //     this.showCloseButton();
  //     return;
  //   }

  //   const container = document.querySelector('.sb-modal__body .sb-pdf-viewer__container');

  //   if (!container) {
  //     console.error('PDF container not found in modal');
  //     this.showCloseButton();
  //     return;
  //   }

  //   try {
  //     const loadingTask = window.pdfjsLib.getDocument(pdfUrl);
  //     const pdf = await loadingTask.promise;
  //     const totalPages = pdf.numPages;
  //     const loadingElement = container.querySelector('.sb-pdf-viewer__loading');
  //     if (loadingElement) loadingElement.remove();

  //     const pagesWrapper = document.createElement('div');
  //     pagesWrapper.className = 'sb-pdf-viewer__pages';
  //     pagesWrapper.style.cssText = 'display: flex; flex-direction: column; gap: 16px; padding: 16px 0;';
  //     container.appendChild(pagesWrapper);

  //     // Render each page sequentially
  //     for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
  //       const page = await pdf.getPage(pageNum);

  //       // --- Scaling logic (unchanged from original) ---
  //       const containerWidth = container.clientWidth - 40;
  //       const viewport = page.getViewport({ scale: 1 });
  //       const devicePixelRatio = window.devicePixelRatio || 1;
  //       const isLargeScreen = window.innerWidth >= 1024;
  //       const isHighDPI = devicePixelRatio > 1;
  //       const baseScale = containerWidth / viewport.width;

  //       let renderScale;
  //       if (isLargeScreen) {
  //         const minScale = isHighDPI ? 1.5 : 1.0;
  //         const maxScale = isHighDPI ? 4.0 : 3.0;
  //         const qualityMultiplier = isHighDPI ? 2.0 : 1.5;
  //         renderScale = Math.min(maxScale, Math.max(minScale, baseScale * devicePixelRatio * qualityMultiplier));
  //       } else {
  //         const minScale = 0.8;
  //         const maxScale = 2.5;
  //         renderScale = Math.min(maxScale, Math.max(minScale, baseScale * devicePixelRatio));
  //       }

  //       const renderViewport = page.getViewport({ scale: renderScale });

  //       // Page wrapper — used for IntersectionObserver targeting
  //       const pageWrapper = document.createElement('div');
  //       pageWrapper.className = 'sb-pdf-viewer__page';
  //       pageWrapper.dataset.pageNum = pageNum;
  //       pageWrapper.style.cssText = 'position: relative; display: flex; justify-content: center;';

  //       const canvas = document.createElement('canvas');
  //       canvas.className = 'sb-pdf-viewer__canvas';
  //       canvas.width = renderViewport.width;
  //       canvas.height = renderViewport.height;
  //       canvas.style.cssText = 'max-width: 100%; height: auto; display: block;';

  //       if (isLargeScreen && isHighDPI) {
  //         canvas.style.imageRendering = 'crisp-edges';
  //         canvas.style.imageRendering = '-webkit-crisp-edges';
  //       }

  //       pageWrapper.appendChild(canvas);
  //       pagesWrapper.appendChild(pageWrapper);

  //       const ctx = canvas.getContext('2d');
  //       ctx.imageSmoothingEnabled = true;
  //       ctx.imageSmoothingQuality = 'high';

  //       await page.render({ canvasContext: ctx, viewport: renderViewport }).promise;
  //     }

  //     this.showCloseButton();
  //   } catch (err) {
  //     console.error('Error rendering PDF:', err);
  //     container.innerHTML = `
  //       <div style="padding: 20px; text-align: center;">
  //         <p>Unable to load PDF. <a href="${pdfUrl}" target="_blank" rel="noopener noreferrer">Open PDF in new tab</a></p>
  //       </div>
  //     `;
  //     this.showCloseButton();
  //   }
  // }

  async initPdfViewer(pdfUrl) {
    if (!window.pdfjsLib) {
      console.error('PDF.js library not found');
      window.open(pdfUrl, '_blank');
      this.showCloseButton();
      return;
    }

    const container = document.querySelector('.sb-modal__body .sb-pdf-viewer__container');

    if (!container) {
      console.error('PDF container not found in modal');
      this.showCloseButton();
      return;
    }

    try {
      const loadingTask = window.pdfjsLib.getDocument(pdfUrl);
      const pdf = await loadingTask.promise;
      const totalPages = pdf.numPages;
      const loadingElement = container.querySelector('.sb-pdf-viewer__loading');
      if (loadingElement) loadingElement.remove();

      const pagesWrapper = document.createElement('div');
      pagesWrapper.className = 'sb-pdf-viewer__pages';
      pagesWrapper.style.cssText = 'display: flex; flex-direction: column; align-items: center; gap: 16px; padding: 16px 0; width: 100%;';
      container.appendChild(pagesWrapper);

      for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
        const page = await pdf.getPage(pageNum);

        // Page's own intrinsic size/orientation — already varies correctly
        // between Letter, A4, legal, landscape, etc. per-page.
        const viewport = page.getViewport({ scale: 1 });
        const pageAspectRatio = viewport.height / viewport.width;

        const containerWidth = container.clientWidth - 40;
        const devicePixelRatio = window.devicePixelRatio || 1;
        const isLargeScreen = window.innerWidth >= 1024;
        const isHighDPI = devicePixelRatio > 1;
        const baseScale = containerWidth / viewport.width;

        let renderScale;
        if (isLargeScreen) {
          const minScale = isHighDPI ? 1.5 : 1.0;
          const maxScale = isHighDPI ? 4.0 : 3.0;
          const qualityMultiplier = isHighDPI ? 2.0 : 1.5;
          renderScale = Math.min(maxScale, Math.max(minScale, baseScale * devicePixelRatio * qualityMultiplier));
        } else {
          const minScale = 0.8;
          const maxScale = 2.5;
          renderScale = Math.min(maxScale, Math.max(minScale, baseScale * devicePixelRatio));
        }

        const renderViewport = page.getViewport({ scale: renderScale });

        // Display size derived from THIS page's own aspect ratio, capped to
        // the available container width. This is what actually prevents the
        // cutoff — it can't be overridden by any fixed-size CSS rule elsewhere.
        const displayWidth = Math.min(containerWidth, viewport.width * baseScale);
        const displayHeight = displayWidth * pageAspectRatio;

        const pageWrapper = document.createElement('div');
        pageWrapper.className = 'sb-pdf-viewer__page';
        pageWrapper.dataset.pageNum = pageNum;
        pageWrapper.style.cssText = `position: relative; display: flex; justify-content: center; width: ${displayWidth}px; max-width: 100%;`;

        const canvas = document.createElement('canvas');
        canvas.className = 'sb-pdf-viewer__canvas';
        canvas.width = renderViewport.width;
        canvas.height = renderViewport.height;
        // Explicit inline px sizing — wins over any external stylesheet rule
        // that assumes a fixed Letter-shaped box.
        canvas.style.width = `${displayWidth}px`;
        canvas.style.height = `${displayHeight}px`;
        canvas.style.maxWidth = '100%';
        canvas.style.display = 'block';

        if (isLargeScreen && isHighDPI) {
          canvas.style.imageRendering = 'crisp-edges';
          canvas.style.imageRendering = '-webkit-crisp-edges';
        }

        pageWrapper.appendChild(canvas);
        pagesWrapper.appendChild(pageWrapper);

        const ctx = canvas.getContext('2d');
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        await page.render({ canvasContext: ctx, viewport: renderViewport }).promise;
      }

      this.showCloseButton();
    } catch (err) {
      console.error('Error rendering PDF:', err);
      container.innerHTML = `
        <div style="padding: 20px; text-align: center;">
          <p>Unable to load PDF. <a href="${pdfUrl}" target="_blank" rel="noopener noreferrer">Open PDF in new tab</a></p>
        </div>
      `;
      this.showCloseButton();
    }
  }

  showCloseButton() {
    const closeButton = document.querySelector('.sb-pdf-viewer__close');
    if (closeButton) {
      closeButton.style.display = 'flex';
    }
  }

  dispatchModalUpdate(modalId, content) {
    document.dispatchEvent(
      new CustomEvent('sb-modal:update', {
        detail: {
          modalId: modalId,
          heading: 'Certificate of Analysis',
          children: content,
        },
      })
    );
  }

  dispatchModalOpen(modalId) {
    document.dispatchEvent(
      new CustomEvent('sb-modal:open', {
        detail: {
          modalId: modalId,
        },
      })
    );
  }
}

if (!customElements.get('sb-pdf-viewer')) {
  customElements.define('sb-pdf-viewer', TdPdfViewer);
}